import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';

const LAST_UPDATED = 'May 3, 2026';
const COMPANY = 'Eagentix Pte Ltd';
const EMAIL = 'contact@eagentix.com';
const ADDRESS = '10, Anson Rd, #29-03A, International Plaza, Singapore 079903';
const SITE = 'https://eagentix.com';
const APP = 'https://app.eagentix.com';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '48px' }}>
      <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', marginBottom: '16px', letterSpacing: '-0.02em' }}>
        {title}
      </h2>
      <div style={{ color: '#475569', fontSize: '0.97rem', lineHeight: 1.8 }}>
        {children}
      </div>
    </section>
  );
}

function P({ children }) {
  return <p style={{ margin: '0 0 14px' }}>{children}</p>;
}

function Ul({ items }) {
  return (
    <ul style={{ margin: '0 0 14px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main style={{ paddingTop: '80px', minHeight: '100vh', background: '#fafafa', fontFamily: 'Inter, sans-serif' }}>
      <SEO
        title="Privacy Policy | Eagentix"
        description="Eagentix Privacy Policy — how we collect, use, and protect your personal data, including data collected via Facebook Login and other OAuth providers."
        pathname="/privacy-policy"
      />

      {/* Header */}
      <div style={{ background: '#0f172a', padding: '64px 20px 56px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Link to="/" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '32px' }}>
            <span>←</span> Back to Home
          </Link>
          <div style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '20px' }}>
            Legal
          </div>
          <h1 style={{ fontSize: '2.75rem', fontWeight: 900, color: 'white', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 16px' }}>
            Privacy Policy
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: 0 }}>
            Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Effective immediately
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '56px 20px 96px' }}>

        <Section title="1. Who We Are">
          <P>
            This Privacy Policy applies to <strong>{COMPANY}</strong> ("Eagentix", "we", "us", or "our"), operator of <a href={SITE} style={{ color: '#0071e3' }}>{SITE}</a> and the Eagentix AI Access Platform at <a href={APP} style={{ color: '#0071e3' }}>{APP}</a>.
          </P>
          <P>
            Our registered address is {ADDRESS}. For privacy-related enquiries, contact us at <a href={`mailto:${EMAIL}`} style={{ color: '#0071e3' }}>{EMAIL}</a>.
          </P>
        </Section>

        <Section title="2. What This Policy Covers">
          <P>
            This policy describes how we collect, use, store, share, and protect personal data when you:
          </P>
          <Ul items={[
            'Visit our website at eagentix.com',
            'Register for or use the Eagentix platform at app.eagentix.com',
            'Sign in using a third-party OAuth provider, including Facebook Login',
            'Contact us by email, form, or any other channel',
            'Interact with our marketing communications',
          ]} />
        </Section>

        <Section title="3. Data We Collect">
          <P><strong>3.1 Data you provide directly</strong></P>
          <Ul items={[
            'Name and email address when you register or contact us',
            'Company name, job title, and use-case description',
            'Messages and attachments you send us',
            'Billing and payment information (processed by our payment provider — we do not store card details)',
          ]} />
          <P><strong>3.2 Data collected via Facebook Login</strong></P>
          <P>
            When you choose to sign in with Facebook, we request only the permissions necessary to create and authenticate your account. Specifically, we request:
          </P>
          <Ul items={[
            'Your public profile (name and profile picture)',
            'Your email address',
          ]} />
          <P>
            We do not request access to your friends list, posts, messages, timeline, or any other Facebook data. The data received from Facebook is used solely to create your Eagentix account, identify you on subsequent logins, and personalise your experience. We do not sell, share, or use this data for advertising purposes.
          </P>
          <P><strong>3.3 Data collected automatically</strong></P>
          <Ul items={[
            'IP address and approximate geolocation',
            'Browser type, operating system, and device identifiers',
            'Pages visited, time on page, and referral source',
            'Cookies and similar tracking technologies (see Section 8)',
          ]} />
          <P><strong>3.4 Data from your use of the platform</strong></P>
          <Ul items={[
            'Prompts, queries, and inputs you submit to AI agents',
            'Agent outputs and conversation history',
            'Usage metrics, feature interactions, and error logs',
          ]} />
        </Section>

        <Section title="4. How We Use Your Data">
          <Ul items={[
            'To create and manage your account',
            'To authenticate you via Facebook Login or other OAuth providers',
            'To provide, operate, and improve the Eagentix platform and agents',
            'To respond to support requests and enquiries',
            'To send transactional emails (account confirmations, password resets)',
            'To send product updates and marketing communications where you have opted in',
            'To detect, prevent, and investigate fraud, abuse, or security incidents',
            'To comply with legal obligations',
            'To analyse usage patterns and improve our services',
          ]} />
        </Section>

        <Section title="5. Legal Basis for Processing (GDPR)">
          <P>Where the GDPR applies, we process your personal data on the following legal bases:</P>
          <Ul items={[
            'Contract: processing necessary to provide the services you have requested',
            'Legitimate interests: security, fraud prevention, product improvement, and direct marketing to existing customers',
            'Consent: marketing emails and non-essential cookies — you may withdraw consent at any time',
            'Legal obligation: compliance with applicable laws and regulations',
          ]} />
        </Section>

        <Section title="6. Sharing Your Data">
          <P>We do not sell your personal data. We share it only in the following circumstances:</P>
          <Ul items={[
            'Service providers: cloud infrastructure, analytics, payment processing, and email delivery providers acting as data processors under contract',
            'AI model providers: prompts may be processed by third-party LLM APIs (e.g. OpenAI, Anthropic) subject to their data processing agreements',
            'Legal requirements: when required by law, court order, or to protect the rights and safety of Eagentix or others',
            'Business transfers: in connection with a merger, acquisition, or sale of assets, with appropriate confidentiality protections',
          ]} />
          <P>
            When you sign in with Facebook, your Facebook data is received directly from Facebook and is not shared with any third party beyond what is described above.
          </P>
        </Section>

        <Section title="7. Data Retention">
          <P>
            We retain your personal data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by emailing <a href={`mailto:${EMAIL}`} style={{ color: '#0071e3' }}>{EMAIL}</a>. We will action deletion requests within 30 days, subject to any legal retention obligations.
          </P>
          <P>
            Data received via Facebook Login is retained only for as long as your account exists. Deleting your Eagentix account removes the association with your Facebook identity from our systems.
          </P>
        </Section>

        <Section title="8. Cookies">
          <P>We use the following categories of cookies:</P>
          <Ul items={[
            'Strictly necessary: session management, authentication, and security — these cannot be disabled',
            'Analytics: Google Analytics to understand how visitors use our site — you may opt out via your browser settings or the Google Analytics opt-out extension',
            'Advertising and measurement: the LinkedIn Insight Tag, which records the pages you visit so we can measure campaign performance and understand, in aggregate, the job functions and industries of our visitors. LinkedIn removes direct identifiers within 7 days and deletes the remaining pseudonymised data within 180 days. You can opt out in your LinkedIn advertising settings',
            'Preferences: remembering your settings and language preferences',
          ]} />
          <P>We do not sell your personal data, and we do not use cookies to build profiles for third-party advertisers beyond the measurement described above.</P>
        </Section>

        <Section title="9. Your Rights">
          <P>Depending on your jurisdiction, you may have the right to:</P>
          <Ul items={[
            'Access the personal data we hold about you',
            'Correct inaccurate or incomplete data',
            'Request deletion of your data ("right to be forgotten")',
            'Object to or restrict certain processing',
            'Data portability — receive your data in a structured, machine-readable format',
            'Withdraw consent at any time where processing is based on consent',
            'Lodge a complaint with your local data protection authority',
          ]} />
          <P>
            To exercise any of these rights, email <a href={`mailto:${EMAIL}`} style={{ color: '#0071e3' }}>{EMAIL}</a> with the subject line "Privacy Request". We will respond within 30 days.
          </P>
        </Section>

        <Section title="10. Data Security">
          <P>
            We implement industry-standard technical and organisational measures to protect your personal data, including encryption in transit (TLS), encryption at rest, access controls, and regular security reviews. No method of transmission over the internet is 100% secure; we cannot guarantee absolute security but we take all reasonable precautions.
          </P>
        </Section>

        <Section title="11. International Transfers">
          <P>
            Eagentix is headquartered in Singapore. Your data may be processed in Singapore, India, or other countries where our service providers operate. Where data is transferred outside the EEA or UK, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or adequacy decisions.
          </P>
        </Section>

        <Section title="12. Children's Privacy">
          <P>
            Our services are not directed at children under 13 (or under 16 in the EEA). We do not knowingly collect personal data from children. If you believe we have inadvertently collected such data, please contact us immediately at <a href={`mailto:${EMAIL}`} style={{ color: '#0071e3' }}>{EMAIL}</a>.
          </P>
        </Section>

        <Section title="13. Third-Party Links">
          <P>
            Our website and platform may contain links to third-party sites. This policy does not apply to those sites. We encourage you to review the privacy policies of any third-party services you use, including Facebook (<a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ color: '#0071e3' }}>facebook.com/privacy/policy</a>).
          </P>
        </Section>

        <Section title="14. Changes to This Policy">
          <P>
            We may update this policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and, where appropriate, notify you by email. Continued use of our services after changes take effect constitutes acceptance of the updated policy.
          </P>
        </Section>

        <Section title="15. Contact Us">
          <P>For any questions, requests, or complaints regarding this Privacy Policy:</P>
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px 28px', marginTop: '8px' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#0f172a' }}>{COMPANY}</p>
            <p style={{ margin: '0 0 6px', color: '#475569' }}>{ADDRESS}</p>
            <p style={{ margin: 0 }}>
              <a href={`mailto:${EMAIL}`} style={{ color: '#0071e3', fontWeight: 600 }}>{EMAIL}</a>
            </p>
          </div>
        </Section>

      </div>
    </main>
  );
}
